/**
 * Simplified version of TipTap's JSONContent type
 * Represents the JSON structure of a TipTap document
 */

import {
  And,
  Constant,
  EraQL,
  Expression,
  If,
  MakeArray,
  MakeStruct,
  Ty,
} from '@cotera/era';
import type { NodeHandler, PositionedNodeHandler } from './handlers';
import type { MarkHandler } from './marks';
import type { GroupHandler } from './handlers/group-handler';
import {
  normalizeJsonNode,
  responseShapeToShorthand,
  shorthandToResponseShape,
} from './json-node';
import z from 'zod/v3';
import { BuildExplorer } from '../commit/build-explorer';
import { HandlerContainer } from './handler-container';
import type { MessageHandler } from './handlers/message-handler';
import { DocumentPromptMarkdownRenderer } from './prompt-markdown-renderer';
import {
  isTipTapTextNode,
  type TextNode,
  type ContentNode,
  type DocumentState,
} from './document-types';

export type {
  Mark,
  TextNode,
  ContentNode,
  DocumentState,
} from './document-types';
import { LlmColConfig } from '../graph/types';
import { LLMMessage } from '@cotera/api';
import { RelativeParser } from '../graph/graph-relative-parser';
import { Result } from 'neverthrow';
import { Assert } from '@cotera/utilities';

const editorTypeEnum = z
  .enum(['tool', 'document', 'expr', 'agent', 'column', 'resource'])
  .optional()
  .catch(undefined);

export class EditorState {
  private handlers: HandlerContainer;

  private readonly promptMarkdown: DocumentPromptMarkdownRenderer;

  private messageHandlers: MessageHandler[];

  constructor(
    private readonly state: DocumentState,
    nodeHandlers?: (NodeHandler | PositionedNodeHandler)[],
    markHandlers?: MarkHandler[],
    groupHandlers?: GroupHandler[],
    messageHandlers?: MessageHandler[]
  ) {
    this.handlers = new HandlerContainer(
      nodeHandlers,
      markHandlers,
      groupHandlers
    );
    this.messageHandlers = messageHandlers ?? [];
    this.promptMarkdown = new DocumentPromptMarkdownRenderer(
      this.handlers,
      this.messageHandlers
    );
  }

  static fromV1(params: {
    columnName: string;
    llmConfig: LlmColConfig;
    tools?: {
      id: string;
      label: string;
    }[];
  }): EditorState {
    const tools = params.tools ?? [];
    const { columnName, llmConfig } = params;

    const content: ContentNode[] = [];

    // Add title as heading level 1
    const trimmedColumnName = columnName.trim();
    if (trimmedColumnName.length > 0) {
      content.push({
        type: 'heading',
        attrs: { level: 1 },
        content: [{ type: 'text', text: trimmedColumnName }],
      });
    }

    // Add imageUrl as attachment node if it exists
    // imageUrl in V1 config can be either:
    // - An EraQL expression (e.g., column reference like "CONTENT_URL")
    // - A plain URL string (e.g., 'https://example.com/image.jpg')
    // We detect URLs by checking if it starts with http:// or https://
    if (llmConfig.imageUrl && llmConfig.imageUrl.length > 0) {
      // If it looks like a URL, treat as freehand (direct URL reference)
      // Otherwise, treat as EraQL expression
      const isUrl =
        llmConfig.imageUrl.startsWith('http://') ||
        llmConfig.imageUrl.startsWith('https://');
      content.push({
        type: 'attachment',
        attrs: {
          value: llmConfig.imageUrl,
          isFreehand: isUrl,
        },
      });
    }

    // Add system message - split by paragraph breaks and handle headings
    const trimmedSystemMessage = llmConfig.systemMessage?.trim() ?? '';
    if (trimmedSystemMessage.length > 0) {
      // Split by double newlines (paragraph boundaries)
      const paragraphs = trimmedSystemMessage.split(/\n\n+/);

      for (const paragraph of paragraphs) {
        const trimmedParagraph = paragraph.trim();
        if (trimmedParagraph.length === 0) {
          continue;
        }

        // Check if paragraph starts with a markdown heading (# to ######)
        const headingMatch = trimmedParagraph.match(/^(#{1,6})\s+(.+)$/);
        if (headingMatch) {
          const level = headingMatch[1]!.length;
          const text = headingMatch[2]!;
          content.push({
            type: 'heading',
            attrs: { level },
            content: [{ type: 'text', text }],
          });
        } else {
          content.push({
            type: 'paragraph',
            content: [{ type: 'text', text: trimmedParagraph }],
          });
        }
      }
    }

    // Add prompt - as plain text if it's a basic string, otherwise as expression node
    if (llmConfig.prompt && llmConfig.prompt.length > 0) {
      const { isBasicString, contents } = EditorState.parseV1Prompt(
        llmConfig.prompt
      );

      if (isBasicString) {
        const trimmedContents = contents.trim();
        if (trimmedContents.length > 0) {
          content.push({
            type: 'paragraph',
            content: [{ type: 'text', text: trimmedContents }],
          });
        }
      } else {
        const trimmedContents = contents.trim();
        if (trimmedContents.length > 0) {
          content.push({
            type: 'expression',
            attrs: { eraql: trimmedContents },
          });
        }
      }
    }

    // Add output type as JSON type builder node (response-shape) if it exists
    if (llmConfig.outputTy) {
      //llmConfig.outputTy might be an object (legacy data)
      if (typeof llmConfig.outputTy === 'object') {
        const jsonNode = shorthandToResponseShape(llmConfig.outputTy);
        content.push({
          type: 'response-shape',
          attrs: {
            jsonNode,
          },
        });
      } else {
        const outputTyParsed = EraQL.parseTy(llmConfig.outputTy);
        if (outputTyParsed.isOk()) {
          const jsonNode = shorthandToResponseShape(outputTyParsed.value);
          content.push({
            type: 'response-shape',
            attrs: {
              jsonNode,
            },
          });
        }
      }
    }

    // Add all tools from v1 config as mention nodes inside a paragraph
    if (llmConfig.tools.length > 0) {
      const toolMentions = llmConfig.tools.map((toolId) => {
        const label = tools.find((t) => t.id === toolId)?.label;
        return {
          type: 'mention' as const,
          attrs: {
            id: label ?? toolId,
            label,
            referenceId: toolId,
            color: 'purple',
            data: JSON.stringify({ t: 'tool', color: 'purple' }),
          },
        };
      });

      content.push({
        type: 'paragraph',
        content: toolMentions,
      });
    }

    const documentState: DocumentState = {
      type: 'doc',
      content,
    };

    return new EditorState(documentState);
  }

  /**
   * Checks if all nodes in the EraQL AST are string literals.
   * A node is considered a string literal if it's either:
   * - A symbol with sym === 'string'
   * - A literal with a string value
   *
   * Note: eraqlAst.value is a WithMeta<EraQlAst> tuple [EraQlAst, TokenMeta],
   * so we access the AST node via value[0].
   */
  private static areAllNodesStringLiterals(
    eraqlAst: ReturnType<typeof EraQL.parseToEraQLAst>
  ): boolean {
    if (!eraqlAst.isOk() || eraqlAst.value === null) {
      return false;
    }

    // eraqlAst.value is a WithMeta<EraQlAst> tuple [EraQlAst, TokenMeta]
    // Extract the AST node (first element of tuple)
    const astNode = (eraqlAst.value as any)[0];
    if (!astNode || typeof astNode !== 'object' || !('t' in astNode)) {
      return false;
    }

    // For a simple AST node, check if it's a string literal
    if (astNode.t === 'literal' && typeof astNode.val === 'string') {
      return true;
    }

    if (astNode.t === 'symbol' && astNode.sym === 'string') {
      return true;
    }

    // For complex nodes (like Call with template display), check all arguments
    if (
      astNode.t === 'call' &&
      astNode.display === 'template' &&
      astNode.args
    ) {
      // Check that ALL arguments are string literals
      for (const argTuple of astNode.args) {
        // argTuple is a WithMeta tuple [EraQlAst, TokenMeta]
        const argNode =
          Array.isArray(argTuple) && argTuple.length > 0
            ? argTuple[0]
            : argTuple;

        if (!argNode || typeof argNode !== 'object' || !('t' in argNode)) {
          return false;
        }

        const isStringSymbol =
          argNode.t === 'symbol' &&
          'sym' in argNode &&
          argNode.sym === 'string';
        const isStringLiteral =
          argNode.t === 'literal' &&
          'val' in argNode &&
          typeof argNode.val === 'string';

        if (!isStringSymbol && !isStringLiteral) {
          return false;
        }
      }

      return true;
    }

    return false;
  }

  title(): string | null {
    return this.state.type === 'doc' &&
      (this.state.content?.length ?? 0) > 0 &&
      this.state.content?.[0]?.type === 'heading' &&
      this.state.content?.[0].attrs?.['level'] === 1
      ? (this.state.content[0].content?.[0] as TextNode)?.text ?? null
      : null;
  }

  getState(): DocumentState {
    return this.state;
  }

  setTitle(title: string): EditorState {
    const hasHeadingAsFirst =
      this.state.content?.[0]?.type === 'heading' &&
      this.state.content?.[0].attrs?.['level'] === 1;

    const newState = {
      ...this.state,
      content: [
        {
          type: 'heading',
          attrs: { level: 1 },
          content: [{ type: 'text', text: title }],
        },
        // If first element is already a heading level 1, skip it (we're replacing it)
        // Otherwise, keep all existing content
        ...(hasHeadingAsFirst
          ? this.state.content?.slice(1) ?? []
          : this.state.content ?? []),
      ],
    };

    return new EditorState(
      newState,
      this.handlers.getAllNodeHandlers(),
      this.handlers.getAllMarkHandlers(),
      this.handlers.getAllGroupHandlersFlat(),
      this.messageHandlers
    );
  }

  /**
   * get all mentions and the mention type currently in the document.
   * Recursively extracts mentions from referenced documents.
   */
  getMentions(graph: BuildExplorer): Array<{
    id: string;
    label: string;
    type:
      | 'tool'
      | 'document'
      | 'expr'
      | 'agent'
      | 'column'
      | 'resource'
      | undefined;
    referenceId?: string;
  }> {
    const mentions: Array<{
      id: string;
      label: string;
      type:
        | 'tool'
        | 'document'
        | 'expr'
        | 'agent'
        | 'column'
        | 'resource'
        | undefined;
      referenceId?: string;
    }> = [];

    // Track visited documents to avoid infinite loops
    const visitedDocuments = new Set<string>();

    const findMentions = (
      nodes: (ContentNode | TextNode)[] | undefined,
      currentDocName?: string
    ): void => {
      if (!Array.isArray(nodes)) {
        return;
      }

      for (const node of nodes) {
        if (typeof node !== 'object' || node === null) {
          continue;
        }
        if (Array.isArray(node)) {
          findMentions(node, currentDocName);
          continue;
        }
        if (isTipTapTextNode(node)) {
          continue;
        }

        if (node.type === 'mention') {
          const attrs = node.attrs;
          const id = attrs?.['id'] ?? 'unknown';
          const label = attrs?.['label'] ?? id;

          // Parse the type from data attribute
          let mentionType: string | undefined;
          if (attrs?.['data']) {
            try {
              const data =
                typeof attrs['data'] === 'string'
                  ? JSON.parse(attrs['data'])
                  : attrs['data'];
              // Handle both "t" (actual structure) and "type" (legacy)
              mentionType = data?.t ?? data?.type;
            } catch {
              // If parsing fails, data might be a plain string type indicator
              mentionType = attrs['data'];
            }
          }

          mentions.push({
            id,
            label,
            type: editorTypeEnum.parse(mentionType),
            referenceId: attrs?.['referenceId'],
          });

          // If this is a document mention, recursively extract mentions
          if (mentionType === 'document' && !visitedDocuments.has(id)) {
            visitedDocuments.add(id);
            const builtNodes = graph.builtNodes();
            const documentNode = builtNodes[id];

            if (documentNode && documentNode.item.t === 'document') {
              // Create a new EditorState from the document's source
              const docEditorState = new EditorState(
                documentNode.item.documentNode.source,
                this.handlers.getAllNodeHandlers(),
                this.handlers.getAllMarkHandlers(),
                this.handlers.getAllGroupHandlersFlat(),
                this.messageHandlers
              );

              // Recursively get mentions from the referenced document
              const docMentions = docEditorState.getMentions(graph);
              mentions.push(...docMentions);
            }
          }
        }

        // Recursively search in child content
        if (node.content) {
          findMentions(node.content, currentDocName);
        }
      }
    };

    findMentions(this.state.content);

    return mentions;
  }

  /**
   * Get all dependencies (mentions and symbol references in expressions/attachments).
   * This is a more comprehensive version of getMentions that also includes references
   * found in expression nodes and attachment nodes.
   *
   * @param graph - The build explorer graph for context
   * @returns Array of unique dependency symbol IDs
   */
  getDependencies(graph: BuildExplorer): string[] {
    const dependencies = new Set<string>();

    // Get all mentions (this already handles recursive document references)
    // Exclude tool mentions - tools are resolved via toolResolver, not via symbol table
    const mentions = this.getMentions(graph);
    for (const mention of mentions) {
      if (mention.type !== 'tool') {
        dependencies.add(mention.id);
      }
    }

    // Extract symbol references from nodes using handlers
    const findDependencies = (
      nodes: (ContentNode | TextNode)[] | undefined
    ): void => {
      if (!Array.isArray(nodes)) {
        return;
      }

      for (const node of nodes) {
        if (typeof node !== 'object' || node === null) {
          continue;
        }
        if (Array.isArray(node)) {
          findDependencies(node);
          continue;
        }
        if (isTipTapTextNode(node)) {
          continue;
        }

        // Use handler to extract dependencies if available
        const handler = this.handlers.getNodeHandler(node.type);
        if (handler && handler.getDependencies) {
          const ctx = {
            graph,
            mode: undefined,
          };
          const refs = handler.getDependencies(node as ContentNode, ctx);
          for (const ref of refs) {
            dependencies.add(ref);
          }
        }

        // Recursively search in child content
        if (node.content) {
          findDependencies(node.content);
        }
      }
    };

    findDependencies(this.state.content);

    return Array.from(dependencies);
  }

  getOutputTy(): Ty.ExtendedAttributeType {
    // Find the response-shape node in the document
    const findResponseShape = (
      nodes: (ContentNode | TextNode)[] | undefined
    ): ContentNode | null => {
      if (!Array.isArray(nodes)) {
        return null;
      }

      for (const node of nodes) {
        if (typeof node !== 'object' || node === null) {
          continue;
        }
        if (Array.isArray(node)) {
          const found = findResponseShape(node);
          if (found) {
            return found;
          }
          continue;
        }
        if (isTipTapTextNode(node)) {
          continue;
        }

        if (node.type === 'response-shape') {
          return node;
        }

        // Recursively search in child content
        if (node.content) {
          const found = findResponseShape(node.content);
          if (found) {
            return found;
          }
        }
      }

      return null;
    };

    const responseShapeNode = findResponseShape(this.state.content);

    if (!responseShapeNode) {
      return Ty.ty('string');
    }

    // Extract the jsonNode attribute
    const jsonNode = responseShapeNode.attrs?.['jsonNode'];

    if (jsonNode === undefined || jsonNode === null) {
      return Ty.ty('string');
    }

    // Repair the stored shape first: it is an opaque node attr, is often LLM
    // authored, and `responseShapeToShorthand` throws on a malformed one.
    return responseShapeToShorthand(normalizeJsonNode(jsonNode));
  }

  /**
   * Replace a reference (symbol name) with a new reference throughout the document.
   * Uses handlers to replace references in nodes that support it.
   *
   * @param oldReference - The old symbol name to replace
   * @param newReference - The new symbol name to use
   * @returns A new EditorState with all references replaced
   */
  replaceReference(oldReference: string, newReference: string): EditorState {
    const replaceInNodes = (
      nodes: (ContentNode | TextNode)[] | undefined
    ): (ContentNode | TextNode)[] | undefined => {
      if (!Array.isArray(nodes)) {
        return undefined;
      }

      return nodes.map((node) => {
        if (typeof node !== 'object' || node === null) {
          return node as ContentNode | TextNode;
        }
        // Text nodes don't need replacement
        if (isTipTapTextNode(node)) {
          return node;
        }

        // Use handler to replace references if available
        const handler = this.handlers.getNodeHandler(node.type);
        let updatedNode: ContentNode | null = null;
        if (handler && handler.replaceReference) {
          updatedNode = handler.replaceReference(
            node as ContentNode,
            oldReference,
            newReference
          );
        }

        // Recursively replace in child content
        let updatedContent = node.content;
        if (node.content) {
          updatedContent = replaceInNodes(node.content);
        }

        // If handler returned an updated node, use it
        if (updatedNode) {
          return {
            ...updatedNode,
            content: updatedContent ?? updatedNode.content,
          };
        }

        // If content was updated, return new node with updated content
        if (updatedContent !== node.content) {
          return {
            ...node,
            content: updatedContent,
          };
        }

        return node;
      });
    };

    const newContent = replaceInNodes(this.state.content);

    return new EditorState(
      {
        ...this.state,
        content: newContent,
      },
      this.handlers.getAllNodeHandlers(),
      this.handlers.getAllMarkHandlers(),
      this.handlers.getAllGroupHandlersFlat(),
      this.messageHandlers
    );
  }

  /**
   * checks if there is any human readable content in the document
   */
  hasContent(): boolean {
    return this.promptMarkdown.hasContent(this.state);
  }

  setContent(content: DocumentState): EditorState {
    return new EditorState(
      content,
      this.handlers.getAllNodeHandlers(),
      this.handlers.getAllMarkHandlers(),
      this.handlers.getAllGroupHandlersFlat(),
      this.messageHandlers
    );
  }

  /**
   * Compare this EditorState with another EditorState for equality.
   * Compares the underlying DocumentState by doing a deep equality check.
   *
   * @param other - The other EditorState to compare with
   * @returns true if the states are equal, false otherwise
   */
  equals(other: EditorState): boolean {
    return JSON.stringify(this.state) === JSON.stringify(other.state);
  }

  /**
   * Get extra messages (e.g., attachments) from the document state.
   * These messages are extracted by message handlers and node handlers, returned as ModelMessage[].
   * Typically used to add file attachments or other non-text content to LLM conversations.
   * @param graph - The build explorer graph for context
   * @param mode - The rendering mode ('simple' or 'expression')
   */
  getExtraMessages(
    graph: BuildExplorer,
    mode?: 'simple' | 'expression'
  ): LLMMessage[] {
    const messages: LLMMessage[] = [];

    const ctx = {
      graph,
      mode: mode ?? undefined,
    };

    // Use message handlers (work on entire DocumentState)
    for (const handler of this.messageHandlers) {
      const handlerMessages = handler.getMessages(this.state, ctx);
      messages.push(...handlerMessages);
    }

    // Also use node handlers' getExtraMessages (work on individual nodes)
    const findNodeMessages = (
      nodes: (ContentNode | TextNode)[] | undefined
    ): void => {
      if (!Array.isArray(nodes)) {
        return;
      }

      for (const node of nodes) {
        if (typeof node !== 'object' || node === null) {
          continue;
        }
        if (Array.isArray(node)) {
          findNodeMessages(node);
          continue;
        }
        if (isTipTapTextNode(node)) {
          continue;
        }

        const handler = this.handlers.getNodeHandler(node.type);
        if (handler && handler.getExtraMessages) {
          const nodeMessages = handler.getExtraMessages(
            node as ContentNode,
            ctx
          );
          messages.push(...nodeMessages);
        }

        // Recursively search in child content
        if (node.content) {
          findNodeMessages(node.content);
        }
      }
    };

    findNodeMessages(this.state.content);

    return messages;
  }

  getMessages(
    graph: BuildExplorer,
    mode: 'simple' | 'expression' = 'simple'
  ): LLMMessage[] {
    const systemMessage = this.toPromptMarkdown(graph, mode);
    const messages = this.getExtraMessages(graph, mode);

    return [
      {
        role: 'system',
        content: systemMessage,
      },
      ...messages,
    ];
  }

  getExpression(
    graph: BuildExplorer,
    parser: RelativeParser
  ): EraQL.ParseRes<Expression> {
    const systemMessage = this.toPromptMarkdown(graph, 'expression');
    const messages = this.getExtraMessages(graph, 'expression');

    const allMessages = [
      {
        role: 'system' as const,
        content: systemMessage,
      },
      ...messages,
    ];

    const parseMessage = (message: LLMMessage): EraQL.ParseRes<Expression> => {
      if (
        message.role === 'system' ||
        message.role === 'user' ||
        message.role === 'assistant'
      ) {
        return parser.parseExpr(message.content).map((content) =>
          MakeStruct({
            role: Constant(message.role, { ty: Ty.ty('string') }),
            content,
          })
        );
      }
      if (message.role === 'file') {
        return parser.parseExpr(message.data).map((data) =>
          MakeStruct({
            role: Constant(message.role, { ty: Ty.ty('string') }),
            filename: Constant(message.filename ?? null, {
              ty: Ty.ty('string'),
            }),
            data,
          })
        );
      }
      if (message.role === 'image') {
        const imageResults = message.images.map((img) =>
          parser.parseExpr(img.url).map((url) =>
            MakeStruct({
              url,
            })
          )
        );
        const combined = Result.combineWithAllErrors(imageResults);
        return combined
          .map((imageExprs) =>
            MakeStruct({
              role: Constant(message.role, { ty: Ty.ty('string') }),
              images: MakeArray(imageExprs),
            })
          )
          .mapErr((errs) => errs.flat());
      }
      return Assert.unreachable(message);
    };

    const systemMessageRes = parser.parseExpr(systemMessage).map((content) =>
      MakeStruct({
        role: Constant('system', { ty: Ty.ty('string') }),
        content,
      })
    );
    const messagesRes = messages.map(parseMessage);
    const allResults = [systemMessageRes, ...messagesRes];

    return Result.combineWithAllErrors(allResults)
      .map((expressions) => {
        // Check if all message contents are null
        const allContentsNull = allMessages.reduce((acc, rawMessage, index) => {
          const messageExpr = expressions[index];
          if (!messageExpr) {
            return Constant(false);
          }

          return And(
            acc,
            getMessageContentField(messageExpr, rawMessage.role).isNull()
          );
        }, Constant(true));

        const messagesArray = MakeArray(
          expressions.map((expr) => expr.cast(Ty.ty('super')))
        );
        return If(allContentsNull, {
          then: Constant(null, { ty: Ty.ty('super') }),
          else: messagesArray.cast('super'),
        });
      })
      .mapErr((errs) => errs.flat());
  }

  /**
   * checks if there is any human readable content in the document besides the title
   */
  hasBodyContent(): boolean {
    return this.promptMarkdown.hasBodyContent(this.state);
  }

  toPromptMarkdown(
    graph: BuildExplorer,
    mode?: 'simple' | 'expression'
  ): string {
    return this.promptMarkdown.render(this.state, { graph, mode });
  }

  /**
   *
   * @param prompt this gnarly method can go away once we have fully migrated the db data to v2
   * @returns { isBasicString: boolean; contents: string; }
   */
  static parseV1Prompt(prompt: string): {
    isBasicString: boolean;
    contents: string;
  } {
    const eraqlAst = EraQL.parseToEraQLAst(prompt);
    if (eraqlAst.isOk()) {
      const ast = eraqlAst.value ?? [];

      if (ast.length === 0) {
        return {
          isBasicString: true,
          contents: prompt,
        };
      }

      const [firstNode, _] = ast;

      // If the first node is a format_nullable call, we need to extract the contents of the template literal
      if (
        firstNode.t === 'call' &&
        firstNode.op[0].t === 'symbol' &&
        firstNode.op[0].sym === 'format_nullable'
      ) {
        const args = firstNode.args;

        const areAllArgsLiterals = args.every(
          (arg) => arg[0].t === 'literal' && typeof arg[0].val === 'string'
        );

        if (areAllArgsLiterals) {
          return {
            isBasicString: true,
            //removes the format_nullable` and the closing backtick
            contents: prompt.slice('format_nullable`'.length, -1),
          };
        }

        return {
          isBasicString: false,
          contents: prompt,
        };
      }
    }

    return {
      isBasicString: true,
      contents: prompt,
    };
  }
}

// Helper function to get the content field from a message expression based on role
const getMessageContentField = (
  messageExpr: Expression,
  role: LLMMessage['role']
): Expression => {
  switch (role) {
    case 'system':
    case 'user':
    case 'assistant':
      return messageExpr.getField('content');
    case 'file':
      return messageExpr.getField('data');
    case 'image':
      return messageExpr.getField('images');
    default:
      return Assert.unreachable(role);
  }
};
